import _ from 'lodash';
import * as turf from '@turf/turf';

import RasterLayerWithClipping from '../RasterLayerWithClipping';
import { acresPerSqMeter } from '../../../constants/units';
import { MAX_POINTS_TO_SAMPLE, RGBA_LENGTH } from './constants';

export const clearBuffer = (gl) => {
  gl.clearColor(0.0, 0.0, 0.0, 1.0);
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT); // eslint-disable-line no-bitwise
};

/**
 * Takes `{ x, y }` coordinates and the `gl` object and translates
 * them to `{ x, y }` pixel coordinates that are adjusted for pixel ratio.
 *
 * This helps avoid floating point issues.
 */
export const translateXY = (point, gl) => {
  const ratio = window.devicePixelRatio;

  const w = gl.drawingBufferWidth;
  const h = gl.drawingBufferHeight;

  const pixelX = Math.floor(point.x * ratio);
  const pixelY = Math.floor(h - point.y * ratio);

  const x = _.clamp(pixelX, 0, w);
  const y = _.clamp(pixelY, 0, h);

  return { x, y };
};

/**
 * Gets the layers corresponding to `layerIds` from `map` and ensures that they
 * are `RasterLayerWithClipping` instances.
 */
export const getClippableTilesetLayers = (map, layerIds) =>
  layerIds.map((layerId) => {
    const layer = map.getLayer(layerId);
    if (!layer) {
      throw new Error(`Layer ${layerId} not found`);
    }
    if (!(layer.implementation instanceof RasterLayerWithClipping)) {
      throw new Error('Layer is not a RasterLayerWithClipping');
    }
    return layer;
  });

/**
 * Just moving the map isn't enough to get the tiles for those map bounds to
 * load immediately. To make sure the tiles load, this function calls the
 * `mapbox-gl-js` code responsible for loading in the tiles correctly, and this
 * approach is a bit faster than calling `map._render()`.
 *
 * We then also await a 0-tick promise, which gives up control of the main
 * thread and allows the browser to finish loading the tiles before we continue.
 *
 * FIXME: awaiting a 0-tick promise is a hacky way to ensure the tiles are
 * loaded, but I haven't been able to find a better solution.
 * - Mark (2024-05-24)
 */
export const ensureTilesLoad = async (map, transform) => {
  if (!map) throw new Error('Map not loaded');
  if (!transform) throw new Error('Transform is required');

  map.style.updateSources(transform);
  map.style._updatePlacement(transform); // eslint-disable-line no-underscore-dangle

  await new Promise((resolve) => setTimeout(resolve, 0));
};

/**
 * Hides all layers except the ones we care about. Returns a cleanup function to
 * restore the layer visibility settings.
 */
export const hideLayersExcept = (layerIds, map) => {
  const original = {};

  // Hide the other layers
  const except = layerIds.concat(['background']); // keep bg layer visible too
  map
    .getStyle()
    .layers.map((l) => l.id)
    .filter((id) => !except.includes(id))
    .forEach((id) => {
      original[id] = map.getLayoutProperty(id, 'visibility');
      map.setLayoutProperty(id, 'visibility', 'none');
    });

  // Store the initial background color
  const initBackgroundColor = map.getPaintProperty(
    'background',
    'background-color',
  );

  // Set background color to black
  map.setPaintProperty('background', 'background-color', 'black');

  // cleanup function to restore
  const restoreLayersVisibility = () => {
    Object.entries(original).forEach(([id, visibility]) => {
      map.setLayoutProperty(id, 'visibility', visibility);
    });
    map.setPaintProperty('background', 'background-color', initBackgroundColor);
  };

  return { restoreLayersVisibility };
};

/**
 * Returns the `mapbox-gl-js` `Tile` class.
 *
 * This is sooooooo hacky. mapbox-gl-js doesn't export the Tile class so we're
 * looking for an existing instance of it and using it to create new instances
 * via a proxy of the contructor. Apologies to future Mark for when we update
 * mapbox-gl-js and this breaks.
 * - Mark (2024-05-17)
 */
export const getMapboxTileClass = (map) => {
  /* eslint-disable no-underscore-dangle */
  const existingTile = Object.values(map.style._mergedSourceCaches)
    .filter((x) => x._tiles && Object.keys(x._tiles).length > 0)
    .flatMap((t) => Object.values(t._tiles))[0];
  /* eslint-enable no-underscore-dangle */

  if (!existingTile) throw new Error('No tiles loaded yet.');

  const TileConstructor = existingTile.constructor;
  return new Proxy(TileConstructor, {
    construct(Target, args) {
      return new Target(...args);
    },
  });
};

/**
 * Generates a grid of points within the features of `featureCollection`.
 * Exported for testing
 */
export const generatePointsInFeatures = ({
  map,
  featureCollection,
  maxPoints = MAX_POINTS_TO_SAMPLE, // reasonable default for performance
}) => {
  const { features } = featureCollection;
  const bounds = turf.bbox(featureCollection);

  // Convert featureCollection to a single MultiPolygon mask:
  const mask =
    features.length <= 1
      ? features[0] || turf.featureCollection([])
      : turf.union(featureCollection);

  /**
   * Here, we generate a grid of pixels within the mask, using 10 meters as
   * the resolution (the suitability data resolution is ~30 meters). If this would
   * result in more than `maxPoints` points, we reduce the resolution
   * until we have fewer points than that limit.
   *
   * We then project these points from map coordinates to pixel coordinates.
   */
  const pointGridSize = Math.max(10, Math.sqrt(turf.area(mask) / maxPoints));
  return turf
    .pointGrid(bounds, pointGridSize, { units: 'meters', mask })
    .features.map((point) => map.project(point.geometry.coordinates));
};

/**
 * Returns a function that will sample the color data from the `map` canvas from
 * a grid of points within a `featureCollection` and return the number of
 * suitable acres.
 *
 * Call this one time once the map is positioned, and then call the returned function
 * once per layer. This way, you can calculate suitability across multiple layers
 * but only call `generatePointsInFeatures` once.
 *
 * `maxPoints` is the maximum number of points we'll sample.
 *
 * NOTE: the returned function uses `gl.readPixels()`, which is synchronous and
 * relatively slow.
 */
export const getCalculateSuitabilityFromMap = ({
  map,
  featureCollection,
  maxPoints,
}) => {
  // Generate the points to sample
  const points = generatePointsInFeatures({
    map,
    featureCollection,
    maxPoints,
  });

  // Calculate the total area of the featureCollection in acres
  const area = turf.area(featureCollection) * acresPerSqMeter;

  // Get the WebGL context
  const { gl } = map.painter.context;

  // Return a function that calculates the suitability
  const calculateSuitabilityFromMap = () => {
    // Pull the color data for the whole canvas
    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;
    const pixels = new Uint8Array(w * h * RGBA_LENGTH);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Then, sample the color at each point in our array
    const suitablePoints = points.filter((point) => {
      const { x, y } = translateXY(point, gl);
      const index = (x + w * y) * RGBA_LENGTH;

      const [r, g, b, a] = pixels.slice(index, index + RGBA_LENGTH);

      // Return true if the pixel is not fully black or fully transparent
      // NOTE: we're using a threshold of 0.001 to account for floating point errors
      return (r > 0.001 || g > 0.001 || b > 0.001) && a > 0.001;
    });

    // Calculate the proportion of suitable points
    const suitabilityProportion = suitablePoints.length / points.length;

    // Return the number of suitable acres
    return suitabilityProportion * area;
  };

  return calculateSuitabilityFromMap;
};
